// declarationRefs — what in the three graphs points at a variable or a
// function, and how to take it away cleanly.
//
// Deleting a declaration used to drop the row from the MY BLUEPRINT rail and
// nothing else: every Get/Set node, every Blend bound to the variable, every
// `Call <fn>` node kept its reference string and silently read `undefined` at
// runtime (GraphEvaluator keeps the authored alpha, EventRuntime sets nothing).
// The Unreal rule is the one people expect — deleting a variable deletes the
// nodes that ARE about it — and this module is that rule as a pure function,
// so the editor's confirm dialog, the undo snapshot and the MCP tool all
// derive from one walk instead of three that can disagree.
//
// Two classes of reference, decided BY FIELD rather than by node type, so a
// node type added tomorrow is classified by what it stores:
//
//   SUBJECT  — `varName` / `variableName` / `functionId`: the node is about the
//              declaration (Get/Set Variable, the array/map ops, an SM rule's
//              varGet, a Call node). Without it the node is meaningless, so the
//              node goes, and every edge touching it at the same level goes too.
//   BINDING  — `alphaVariable` / `boolVariable` / `valueVariable`: an OPTIONAL
//              binding on a node that carries its own authored value or pin (a
//              Blend's alpha, Blend by Bool's toggle, a rig-chain value). The
//              node stays and the field is cleared, which is exactly the
//              fallback the evaluators already take when the variable is
//              missing — but now it is visible in the node instead of silent.
//
// The two lists together must equal nodeClipboard's VARIABLE_FIELDS (the
// paste-time `requires` manifest); a unit test holds them together.
//
// Depth mirrors nodeClipboard.walkNodes and is all real: a state machine's
// `innerNodes` hold `state` nodes with inner graphs of their own, and its
// `innerEdges` carry a rule graph per transition. Edges are removed at the
// SAME level as the node they touch — a rule graph's edge lives in
// `ruleGraph.edges`, a state's in `state.data.innerEdges`.
//
// Removal is by NODE IDENTITY, not id. Ids are unique within one level only:
// every function body has an `entry`, and every transition's rule graph in the
// shipped conversational blueprint holds a varGet with id `vg`. Deleting "the
// node with id vg" per graph would take a rule reading Speed down with the
// rule reading ConversationalState. A ref therefore carries the node object it
// found, and the walk that removes it is over the same objects.
import { VARIABLE_FIELDS } from './nodeClipboard.js'
import { nodeLabel, describeLocation } from './graphNodeSearch.js'

export const VARIABLE_SUBJECT_FIELDS = Object.freeze(['varName', 'variableName'])
export const VARIABLE_BINDING_FIELDS = Object.freeze(['alphaVariable', 'boolVariable', 'valueVariable'])
export const FUNCTION_SUBJECT_FIELDS = Object.freeze(['functionId'])

// Every VARIABLE_FIELDS entry must be classified. Checked at module load so a
// field added to the clipboard list without a class here fails the first
// import, not the first delete.
for (const f of VARIABLE_FIELDS) {
  if (!VARIABLE_SUBJECT_FIELDS.includes(f) && !VARIABLE_BINDING_FIELDS.includes(f)) {
    throw new Error(`declarationRefs: VARIABLE_FIELDS entry "${f}" is neither subject nor binding`)
  }
}

const GRAPH_ORDER = ['body', 'face', 'event']

function fieldsFor(kind) {
  if (kind === 'variable') return { subject: VARIABLE_SUBJECT_FIELDS, binding: VARIABLE_BINDING_FIELDS }
  if (kind === 'function') return { subject: FUNCTION_SUBJECT_FIELDS, binding: [] }
  throw new Error(`declarationRefs: unknown kind "${kind}" (variable | function)`)
}

/**
 * Visit every node of a graph at every depth. `visit(node, container)` gets
 * the INNERMOST container holding the node (null at top level), shaped like
 * graphNodeSearch's: `{ kind, id, name }` with kind ∈ group | state | rule |
 * function. A state machine is reported as kind 'group' (it is a container of
 * states); a state's own inner graph is 'state'; a transition rule graph is
 * 'rule'.
 */
function walkGraph(nodes, visit, container = null) {
  for (const node of nodes ?? []) {
    visit(node, container)
    const inner = node?.data?.innerNodes
    if (Array.isArray(inner)) {
      const kind = node.type === 'state' ? 'state' : 'group'
      walkGraph(inner, visit, { kind, id: node.id, name: nodeLabel(node) })
    }
    for (const edge of node?.data?.innerEdges ?? []) {
      const rule = edge?.data?.ruleGraph?.nodes
      if (Array.isArray(rule)) {
        // Name the transition by its states, so seventeen rules on one machine
        // do not all read "ConversationalState transition".
        const stateName = (id) => { const st = inner?.find?.(n => n.id === id); return st ? nodeLabel(st) : id }
        const name = `${nodeLabel(node)} transition ${stateName(edge.source)} → ${stateName(edge.target)}`
        walkGraph(rule, visit, { kind: 'rule', id: edge.id, name })
      }
    }
  }
}

function refOf(graph, node, container, field, action) {
  return { graph, node, nodeId: node.id, nodeType: node.type, label: nodeLabel(node), field, action, container }
}

/**
 * Every node that names the declaration, with what removing it means.
 *
 * @param {object} graphs { body:{nodes,edges}, face:{nodes,edges}, event:{nodes,edges,functions} }
 * @param {{kind:'variable'|'function', key:string}} decl  a variable's NAME or a function's ID —
 *        the string the nodes actually store
 * @returns {Array<{graph, nodeId, nodeType, label, field, action:'delete'|'clear', container}>}
 */
export function findDeclarationRefs(graphs, { kind, key }) {
  const { subject, binding } = fieldsFor(kind)
  const out = []
  if (typeof key !== 'string' || key === '') return out

  const visitFor = (graph) => (node, container) => {
    const data = node?.data
    if (!data || typeof data !== 'object') return
    for (const f of subject) if (data[f] === key) { out.push(refOf(graph, node, container, f, 'delete')); return }
    for (const f of binding) if (data[f] === key) { out.push(refOf(graph, node, container, f, 'clear')); return }
  }

  for (const g of GRAPH_ORDER) walkGraph(graphs?.[g]?.nodes, visitFor(g))
  for (const [id, fn] of Object.entries(graphs?.event?.functions ?? {})) {
    walkGraph(fn?.nodes, visitFor('event'), { kind: 'function', id, name: fn?.name ?? id })
  }
  return out
}

/**
 * Apply a set of refs to one node list + its edge list, at this level and
 * every level below. Returns the SAME arrays when nothing at any depth changed,
 * so an untouched graph stays referentially identical (React and the autosave
 * both key off identity).
 */
function applyToLevel(nodes, edges, deleteNodes, clearByNode) {
  let changed = false
  const deleteIds = new Set()
  for (const node of nodes ?? []) if (deleteNodes.has(node)) deleteIds.add(node.id)
  const nextNodes = []
  for (const node of nodes ?? []) {
    if (deleteNodes.has(node)) { changed = true; continue }
    let next = node
    const clearField = clearByNode.get(node)
    if (clearField) {
      next = { ...node, data: { ...node.data, [clearField]: '' } }
      changed = true
    }
    const inner = node?.data?.innerNodes
    const innerEdges = node?.data?.innerEdges
    if (Array.isArray(inner) || Array.isArray(innerEdges)) {
      const sub = applyToLevel(inner ?? [], innerEdges ?? [], deleteNodes, clearByNode)
      let subEdges = sub.edges
      // Rule graphs ride on this level's edges.
      let edgesChanged = sub.edges !== (innerEdges ?? [])
      const ruled = subEdges.map((edge) => {
        const rule = edge?.data?.ruleGraph
        if (!rule || !Array.isArray(rule.nodes)) return edge
        const r = applyToLevel(rule.nodes, rule.edges ?? [], deleteNodes, clearByNode)
        if (r.nodes === rule.nodes && r.edges === (rule.edges ?? [])) return edge
        edgesChanged = true
        return { ...edge, data: { ...edge.data, ruleGraph: { ...rule, nodes: r.nodes, edges: r.edges } } }
      })
      if (edgesChanged) subEdges = ruled
      if (sub.nodes !== (inner ?? []) || edgesChanged) {
        next = { ...next, data: { ...next.data } }
        if (Array.isArray(inner)) next.data.innerNodes = sub.nodes
        if (Array.isArray(innerEdges) || edgesChanged) next.data.innerEdges = subEdges
        changed = true
      }
    }
    nextNodes.push(next)
  }
  let nextEdges = edges ?? []
  if (deleteIds.size) {
    const kept = nextEdges.filter(e => !deleteIds.has(e.source) && !deleteIds.has(e.target))
    if (kept.length !== nextEdges.length) { nextEdges = kept; changed = true }
  }
  return changed ? { nodes: nextNodes, edges: nextEdges } : { nodes: nodes ?? [], edges: edges ?? [] }
}

/**
 * Remove the references: subject nodes deleted with their edges, binding
 * fields cleared. Pure — returns new graph objects only for the graphs (and
 * functions) that actually changed; everything else is the same object.
 *
 * Node ids are unique per graph (the minter recurses into containers) but NOT
 * across graphs or function bodies (every function has an `entry`), so the
 * refs are applied per graph and per function using that scope's own ids.
 */
export function removeDeclarationRefs(graphs, refs) {
  const deleteNodes = new Set()
  const clearByNode = new Map()
  let deleted = 0, cleared = 0
  for (const r of refs ?? []) {
    if (!r?.node) continue
    if (r.action === 'delete') { deleteNodes.add(r.node); deleted++ }
    else if (r.action === 'clear') { clearByNode.set(r.node, r.field); cleared++ }
  }
  const out = { ...graphs }
  if (deleteNodes.size === 0 && clearByNode.size === 0) return { graphs: out, deleted, cleared }

  for (const g of GRAPH_ORDER) {
    const src = graphs?.[g]
    if (!src) continue
    const res = applyToLevel(src.nodes, src.edges, deleteNodes, clearByNode)
    if (res.nodes !== (src.nodes ?? []) || res.edges !== (src.edges ?? [])) {
      out[g] = { ...src, nodes: res.nodes, edges: res.edges }
    }
  }
  const srcFns = graphs?.event?.functions
  if (srcFns) {
    let fnChanged = false
    const functions = { ...srcFns }
    for (const [fnId, fn] of Object.entries(srcFns)) {
      const res = applyToLevel(fn?.nodes, fn?.edges, deleteNodes, clearByNode)
      if (res.nodes !== (fn?.nodes ?? []) || res.edges !== (fn?.edges ?? [])) {
        functions[fnId] = { ...fn, nodes: res.nodes, edges: res.edges }
        fnChanged = true
      }
    }
    if (fnChanged) out.event = { ...(out.event ?? graphs.event), functions }
  }
  return { graphs: out, deleted, cleared }
}

/**
 * Plain-text lines for the confirm dialog: one per location, grouped by
 * graph, each naming the nodes and whether they are deleted or unbound.
 */
export function describeRefs(refs) {
  const groups = new Map()
  for (const r of refs ?? []) {
    const where = describeLocation(r)
    if (!groups.has(where)) groups.set(where, [])
    groups.get(where).push(r)
  }
  // Identical entries collapse to one with a count: one state machine can hold
  // seventeen rules that each read the variable through a varGet.
  const tally = (list, fmt) => {
    const counts = new Map()
    for (const r of list) { const k = fmt(r); counts.set(k, (counts.get(k) ?? 0) + 1) }
    return Array.from(counts, ([k, n]) => (n > 1 ? `${k} ×${n}` : k)).join(', ')
  }
  const named = (r) => (r.label && r.label !== r.nodeType ? `${r.nodeType} "${r.label}"` : r.nodeType)
  const lines = []
  for (const [where, list] of groups) {
    const del = list.filter(r => r.action === 'delete')
    const clr = list.filter(r => r.action === 'clear')
    const parts = []
    if (del.length) parts.push(`delete ${tally(del, named)}`)
    if (clr.length) parts.push(`unbind ${tally(clr, r => `${named(r)}.${r.field}`)}`)
    lines.push(`${where}: ${parts.join('; ')}`)
  }
  return lines
}

/** `Name_Copy`, then `Name_Copy2`, … — a copy of a copy does not stack suffixes. */
export function uniqueCopyName(name, taken) {
  const set = new Set(taken ?? [])
  const base = String(name ?? '').replace(/_Copy\d*$/, '') || 'Copy'
  let candidate = `${base}_Copy`
  for (let i = 2; set.has(candidate); i++) candidate = `${base}_Copy${i}`
  return candidate
}

function deepCopy(v) {
  return v === undefined ? undefined : JSON.parse(JSON.stringify(v))
}

/** A new variable definition beside `def`: same type shape, copied values, fresh id, unique name. */
export function duplicateVariableDef(def, variables, mintId) {
  const dup = deepCopy(def)
  dup.id = mintId()
  dup.name = uniqueCopyName(def.name, (variables ?? []).map(v => v.name))
  return dup
}

/**
 * A new function beside `fn`: deep-copied body under a fresh id and unique
 * name. Each function body is its own id namespace (every one has an `entry`),
 * so inner ids are kept; only the return node, which embeds the function id by
 * convention (`<fnId>_ret`), follows the new id — with its edges.
 */
export function duplicateFunctionDef(fn, functions, mintId) {
  const id = mintId()
  const dup = deepCopy(fn)
  dup.id = id
  dup.name = uniqueCopyName(fn.name, Object.values(functions ?? {}).map(f => f.name))
  const oldRet = `${fn.id}_ret`
  const newRet = `${id}_ret`
  dup.nodes = (dup.nodes ?? []).map(n => n.id === oldRet ? { ...n, id: newRet } : n)
  dup.edges = (dup.edges ?? []).map(e => ({
    ...e,
    source: e.source === oldRet ? newRet : e.source,
    target: e.target === oldRet ? newRet : e.target,
  }))
  return dup
}
